/**
 * Help Center static content service
 * Fetches pre-generated JSON files from GCS for help center content
 */

import { HelpCategory, HelpArticleSummary, HelpArticle, HelpQuickAction } from '../types';

const STATIC_BASE_URL = import.meta.env.VITE_STATIC_CONTENT_URL ||
  'https://storage.googleapis.com/foxie-systems-dev.firebasestorage.app/static';

const HELP_BASE_URL = `${STATIC_BASE_URL}/help`;

/**
 * Cache for help content with TTL
 */
interface CacheEntry<T> {
  data: T;
  expiresAt: number;
}

const cache = new Map<string, CacheEntry<any>>();

/**
 * Fetch static JSON file with caching
 */
async function fetchHelpStatic<T>(path: string, cacheTTL: number = 3600000): Promise<T> {
  const now = Date.now();
  const cacheKey = `help:${path}`;
  const cached = cache.get(cacheKey);

  // Return cached data if still valid
  if (cached && cached.expiresAt > now) {
    console.log(`[HelpContent] Cache hit: ${path}`);
    return cached.data;
  }

  console.log(`[HelpContent] Fetching: ${path}`);

  try {
    const response = await fetch(`${HELP_BASE_URL}/${path}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch ${path}: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    // Store in cache
    cache.set(cacheKey, {
      data,
      expiresAt: now + cacheTTL,
    });

    return data;
  } catch (error) {
    console.error(`[HelpContent] Error fetching ${path}:`, error);

    // If we have expired cache, return it as fallback
    if (cached) {
      console.warn(`[HelpContent] Using expired cache for ${path}`);
      return cached.data;
    }

    throw error;
  }
}

/**
 * Help manifest structure
 */
export interface HelpManifest {
  version: string;
  generatedAt: any;
  checksum: string;
  files: {
    categories: string;
    articles: string;
    popular: string;
    quickActions: string;
  };
}

/**
 * Popular article summary (for sidebar)
 */
export interface PopularArticle {
  id: string;
  slug: string;
  title: string;
  categoryId: string;
  viewCount: number;
}

/**
 * Get help manifest
 */
export async function getHelpManifest(): Promise<HelpManifest> {
  return fetchHelpStatic<HelpManifest>('manifest.json', 300000); // 5 minute cache
}

/**
 * Get help categories
 */
export async function getHelpCategories(): Promise<HelpCategory[]> {
  return fetchHelpStatic<HelpCategory[]>('categories.json', 3600000); // 1 hour cache
}

/**
 * Get all help articles summary
 */
export async function getHelpArticles(): Promise<HelpArticleSummary[]> {
  return fetchHelpStatic<HelpArticleSummary[]>('articles.json', 3600000); // 1 hour cache
}

/**
 * Get popular articles
 */
export async function getPopularArticles(): Promise<PopularArticle[]> {
  return fetchHelpStatic<PopularArticle[]>('popular.json', 3600000); // 1 hour cache
}

/**
 * Get quick actions
 */
export async function getQuickActions(): Promise<HelpQuickAction[]> {
  return fetchHelpStatic<HelpQuickAction[]>('quick-actions.json', 86400000); // 24 hour cache
}

/**
 * Get a single article by slug
 */
export async function getArticleBySlug(slug: string): Promise<HelpArticle | null> {
  try {
    return await fetchHelpStatic<HelpArticle>(`articles/${slug}.json`, 3600000); // 1 hour cache
  } catch (error) {
    console.error(`[HelpContent] Article not found: ${slug}`);
    return null;
  }
}

/**
 * Clear help content cache
 */
export function clearHelpCache(): void {
  const keysToDelete: string[] = [];
  cache.forEach((_, key) => {
    if (key.startsWith('help:')) {
      keysToDelete.push(key);
    }
  });
  keysToDelete.forEach(key => cache.delete(key));
  console.log('[HelpContent] Cache cleared');
}

/**
 * Get articles by category
 */
export function filterArticlesByCategory(
  articles: HelpArticleSummary[],
  categoryId: string
): HelpArticleSummary[] {
  return articles.filter(a => a.categoryId === categoryId);
}

/**
 * Get featured articles
 */
export function getFeaturedArticles(articles: HelpArticleSummary[]): HelpArticleSummary[] {
  return articles.filter(a => a.featured);
}

/**
 * Sort articles
 */
export function sortArticles(
  articles: HelpArticleSummary[],
  sortBy: 'title' | 'viewCount' | 'publishedAt' = 'viewCount',
  order: 'asc' | 'desc' = 'desc'
): HelpArticleSummary[] {
  const sorted = [...articles].sort((a, b) => {
    let aVal: any;
    let bVal: any;

    switch (sortBy) {
      case 'title':
        aVal = a.title.toLowerCase();
        bVal = b.title.toLowerCase();
        break;
      case 'viewCount':
        aVal = a.viewCount;
        bVal = b.viewCount;
        break;
      case 'publishedAt':
        aVal = a.publishedAt || '';
        bVal = b.publishedAt || '';
        break;
    }

    if (order === 'asc') {
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    } else {
      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
    }
  });

  return sorted;
}

/**
 * Group articles by category
 */
export function groupArticlesByCategory(
  articles: HelpArticleSummary[],
  categories: HelpCategory[]
): Map<string, { category: HelpCategory; articles: HelpArticleSummary[] }> {
  const grouped = new Map<string, { category: HelpCategory; articles: HelpArticleSummary[] }>();

  // Initialize with all categories
  categories.forEach(category => {
    grouped.set(category.id, { category, articles: [] });
  });

  // Group articles
  articles.forEach(article => {
    const group = grouped.get(article.categoryId);
    if (group) {
      group.articles.push(article);
    }
  });

  return grouped;
}

/**
 * Track article view (increment view count)
 * This is a fire-and-forget operation - we don't want to block the UI
 */
export async function trackArticleView(slug: string): Promise<void> {
  try {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/foxie-systems-dev/us-central1/api';

    // Fire and forget - don't await or block
    fetch(`${apiUrl}/help/articles/${encodeURIComponent(slug)}/view`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    }).catch(err => {
      // Silently ignore errors - view tracking is not critical
      console.debug('[HelpContent] View tracking failed:', err);
    });
  } catch (error) {
    // Silently ignore errors - view tracking is not critical
    console.debug('[HelpContent] View tracking error:', error);
  }
}
